import { useEffect, useRef, useState } from "react";
import { Users, BookOpen, HandHeart, Calendar, Heart, Star, Building2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Animated count-up impact strip.
 * Content (heading + each stat) is fully admin-controlled via
 *   GET /api/impact-stats
 * Falls back gracefully to sensible defaults if the endpoint is unreachable
 * so the homepage never shows a blank section.
 */
const ICON_MAP = {
  users: Users,
  book: BookOpen,
  "hand-heart": HandHeart,
  calendar: Calendar,
  heart: Heart,
  star: Star,
  mosque: Building2,
};

const FALLBACK = {
  eyebrow: "Alhamdulillah · By Allah's Grace",
  heading: "Our community, in numbers",
  subheading: "Every prayer, every student, every meal — a small ayah in the long story of our masjid.",
  stats: [
    { id: "f1", label: "Weekly worshippers", value: 500,  suffix: "+", icon: "users",      description: "" },
    { id: "f2", label: "Students enrolled",  value: 120,  suffix: "+", icon: "book",       description: "" },
    { id: "f3", label: "Meals served / yr",  value: 1400, suffix: "+", icon: "hand-heart", description: "" },
    { id: "f4", label: "Annual events",      value: 35,   suffix: "+", icon: "calendar",   description: "" },
  ],
};

function CountUp({ end, suffix = "", duration = 1600, active }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setN(Math.round(eased * end));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, end, duration]);
  return <span>{n.toLocaleString()}{suffix}</span>;
}

export default function ImpactCounters() {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const [data, setData] = useState(FALLBACK);

  useEffect(() => {
    api.get("/impact-stats").then((r) => {
      // Only use the API payload if there's at least one published stat —
      // otherwise keep the fallback so the section never goes blank.
      if (r.data?.stats?.length) setData(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActive(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // If admin has unpublished every stat, hide the whole section.
  if (!data.stats || data.stats.length === 0) return null;

  // Keep the grid balanced: 2 cols on mobile, scale on desktop.
  // Note: full classes (not interpolated) so Tailwind JIT picks them up.
  const lgCols = Math.min(data.stats.length, 4);
  const gridCols =
    lgCols === 1 ? "grid-cols-1"
    : lgCols === 2 ? "grid-cols-2"
    : lgCols === 3 ? "grid-cols-2 lg:grid-cols-3"
    : "grid-cols-2 lg:grid-cols-4";

  return (
    <section
      ref={ref}
      className="relative bg-navy text-cream py-20 md:py-24 px-6 md:px-10 overflow-hidden"
      data-testid="impact-counters"
    >
      <div className="absolute inset-0 pattern-girih opacity-[0.08] pointer-events-none" />
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-gold/[0.06] blur-3xl pointer-events-none" />
      <div className="absolute top-12 left-[6%] w-1.5 h-1.5 rounded-full bg-gold/60 home-float" />
      <div className="absolute bottom-16 right-[10%] w-2 h-2 rounded-full bg-gold/40 home-float-delayed" />
      <div className="relative max-w-7xl mx-auto">
        {(data.eyebrow || data.heading || data.subheading) && (
          <div className="text-center max-w-2xl mx-auto mb-14">
            {data.eyebrow && (
              <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">
                {data.eyebrow}
              </div>
            )}
            {data.heading && (
              <h2 className="font-serif text-4xl md:text-5xl mt-3 text-cream">
                {data.heading}
              </h2>
            )}
            {data.subheading && (
              <p className="mt-4 text-cream/70 font-light leading-relaxed">
                {data.subheading}
              </p>
            )}
          </div>
        )}
        <div className={`grid ${gridCols} gap-6 md:gap-8`}>
          {data.stats.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || Users;
            return (
              <div
                key={s.id}
                data-testid={`impact-stat-${i}`}
                className="counter-pop relative rounded-3xl bg-cream/[0.04] border border-gold/15 p-7 md:p-8 backdrop-blur-sm hover:border-gold/40 hover:bg-cream/[0.07] transition-colors"
                style={{ animationDelay: `${i * 120}ms` }}
              >
                <Icon size={26} className="text-gold mb-4" />
                <div className="font-serif text-4xl md:text-5xl text-cream tabular-nums">
                  <CountUp end={Number(s.value) || 0} suffix={s.suffix || ""} active={active} />
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-cream/70 font-semibold">
                  {s.label}
                </div>
                {s.description && (
                  <div className="mt-2 text-xs text-cream/55 leading-relaxed">
                    {s.description}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
